import React from "react";
import { useQuery } from "@apollo/client";
import { Box, CircularProgress, Typography } from "@mui/material";
import { activeUsersByCityReport, activeUsersByCityReportByRange } from "./graphql";
import { AnalyticsReportQuery } from "./analyticsTypes";
import { parseMetricValue } from "./analyticsUtils";
import { MetricList, MetricListItem } from "./MetricList";

const findHeaderIndex = (headers: string[], target: string) =>
  headers.findIndex((header) => header.toLowerCase() === target.toLowerCase());

interface CityActiveUsersCardProps {
  limit?: number;
  startDate?: string;
  endDate?: string;
}

export const CityActiveUsersCard = ({ limit, startDate, endDate }: CityActiveUsersCardProps) => {
  const hasRange = !!startDate && !!endDate;
  const { data, loading, error } = useQuery<AnalyticsReportQuery>(
    hasRange ? activeUsersByCityReportByRange : activeUsersByCityReport,
    hasRange ? { variables: { startDate, endDate } } : undefined
  );
  const report = data?.analyticsReport ?? null;

  const items = React.useMemo<MetricListItem[]>(() => {
    if (!report) return [];
    const dimensionIndex = Math.max(
      0,
      findHeaderIndex(report.dimensionHeaders, "city")
    );
    const metricIndex = Math.max(
      0,
      findHeaderIndex(report.metricHeaders, "activeUsers")
    );

    const mapped = report.rows.map((row) => {
      const label = row.dimensionValues[dimensionIndex] || "Unknown";
      const value =
        metricIndex >= 0 ? parseMetricValue(row.metricValues[metricIndex]) : 0;
      return { label, value };
    });

    return mapped
      .filter((item) => item.label.length > 0)
      .sort((a, b) => b.value - a.value);
  }, [report]);

  if (loading) {
    return (
      <Box sx={{ height: 180, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography variant="body2" color="error">
        Failed to load chart.
      </Typography>
    );
  }

  return (
    <MetricList
      items={items}
      labelHeader="City"
      valueLabel="Active Users"
      limit={limit}
    />
  );
};
